/**
 * Enter funcionando como Tab nos campos de edição (InputText, InputNumber, InputMask, Calendar,
 * Dropdown, MultiSelect, AutoComplete, Checkbox, select, etc.). Shift+Enter volta para o campo anterior.
 *
 * Não interfere quando:
 * - o foco está em textarea, botão, link ou editor de texto (o Enter mantém o comportamento normal);
 * - o campo está dentro de um <form> (o Enter continua enviando o formulário, ex.: login e pesquisas);
 * - o campo tem um tratamento de tecla próprio na tela (onKeyDown/onKeyPress/onKeyUp), por exemplo
 *   pesquisar ao teclar Enter ou leitor de código de barras;
 * - o campo, ou um elemento acima dele, tem o atributo data-enter-normal.
 *
 * Nos componentes do PrimeReact (Dropdown, AutoComplete, Calendar...) o Enter primeiro faz a ação do
 * componente (fechar a lista, selecionar o item destacado) e depois passa para o próximo campo.
 *
 * Grids (DataTable com seleção): Enter sobre uma linha seleciona o registro e aciona o botão de edição
 * mais próximo da grid ("Editar", ou "Alterar ..." como "Alterar Ítem"), quando existir.
 */

const COMPONENTES_PRIMEREACT = [
  '.p-dropdown', '.p-multiselect', '.p-autocomplete', '.p-calendar', '.p-inputnumber',
  '.p-cascadeselect', '.p-treeselect', '.p-checkbox', '.p-radiobutton', '.p-inputswitch',
  '.p-chips', '.p-colorpicker', '.p-rating',
].join(', ');

const TIPOS_INPUT_IGNORADOS = ['button', 'submit', 'reset', 'file', 'image', 'hidden'];

const SELETOR_CAMPOS = 'input, select, textarea';

function eCampoDeEdicao(el) {
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toUpperCase();
  if (tag === 'SELECT') return true;
  if (tag === 'INPUT') return !TIPOS_INPUT_IGNORADOS.includes((el.type || '').toLowerCase());
  return false;
}

function propsReact(el) {
  const chave = Object.keys(el).find(k => k.startsWith('__reactProps$') || k.startsWith('__reactEventHandlers$'));
  return chave ? el[chave] : null;
}

function temTratamentoDeTeclaProprio(el) {
  const props = propsReact(el);
  return !!(props && (props.onKeyDown || props.onKeyPress || props.onKeyUp));
}

function estaVisivel(el) {
  if (el.disabled) return false;
  if (el.closest('.p-datepicker, .p-dropdown-panel, .p-multiselect-panel, .p-autocomplete-panel, [aria-hidden="true"]')) return false;
  const style = window.getComputedStyle(el);
  if (style.visibility === 'hidden' || style.display === 'none') return false;
  // Inputs "escondidos" dos componentes PrimeReact (p-hidden-accessible) recebem foco normalmente
  if (el.closest('.p-hidden-accessible')) {
    const componente = el.closest(COMPONENTES_PRIMEREACT);
    return !!(componente && componente.getClientRects().length);
  }
  return el.getClientRects().length > 0;
}

function camposNavegaveis(escopo) {
  return Array.from(escopo.querySelectorAll(SELETOR_CAMPOS)).filter(el => {
    if (el.tagName.toUpperCase() === 'TEXTAREA') return !el.readOnly; // textarea entra na sequência, mas o Enter dentro dela é normal
    if (!eCampoDeEdicao(el)) return false;
    if (el.tabIndex < 0) return false;
    return estaVisivel(el);
  });
}

// "Alterar ..." que não são edição do registro selecionado
const TITULOS_ALTERAR_IGNORADOS = ['alterar senha', 'alterar situação', 'alterar cor do sistema', 'alterar estoque',
  'alterar o número da ordem produção'];

function eBotaoDeEdicao(botao) {
  const titulo = (botao.getAttribute('title') || '').trim().toLowerCase();
  if (titulo === 'editar') return 2;
  if (titulo.startsWith('alterar') && !TITULOS_ALTERAR_IGNORADOS.includes(titulo)) return 1;
  return 0;
}

function botaoDeEdicaoDaGrid(linha) {
  const limite = linha.closest('.p-dialog, .modal-dialog, [role="dialog"]');
  for (let no = linha.closest('.p-datatable'); no && no !== document.body; no = no.parentElement) {
    const candidatos = Array.from(no.querySelectorAll('button[title]'))
      .filter(b => !b.disabled && b.getClientRects().length > 0 && eBotaoDeEdicao(b) > 0
        && !b.closest('.p-datatable')); // botões fora das linhas da grid
    if (candidatos.length) {
      candidatos.sort((a, b) => eBotaoDeEdicao(b) - eBotaoDeEdicao(a));
      return candidatos[0];
    }
    if (no === limite) break;
  }
  return null;
}

function aoTeclarNaGrid(e, linha) {
  const botao = botaoDeEdicaoDaGrid(linha);
  if (!botao) return;
  e.preventDefault();
  // A linha já foi selecionada pelo DataTable neste mesmo Enter; aguarda a tela atualizar a seleção
  setTimeout(() => botao.click(), 0);
}

function aoTeclar(e) {
  if (e.key !== 'Enter' && e.code !== 'NumpadEnter') return;
  if (e.ctrlKey || e.altKey || e.metaKey || e.isComposing) return;

  const el = e.target;

  const linha = el && el.closest ? el.closest('tr.p-selectable-row') : null;
  if (linha && (el === linha || el.tagName.toUpperCase() === 'TD') && !el.closest('[data-enter-normal]')) {
    aoTeclarNaGrid(e, linha);
    return;
  }
  if (!eCampoDeEdicao(el)) return;
  if (el.closest('form, [data-enter-normal]')) return;

  const componente = el.closest(COMPONENTES_PRIMEREACT);
  if (!componente && temTratamentoDeTeclaProprio(el)) return;

  const escopo = el.closest('.p-dialog, .modal-dialog, [role="dialog"]') || document;
  const campos = camposNavegaveis(escopo);
  const atual = campos.indexOf(el);
  if (atual < 0) return;

  const proximo = campos[e.shiftKey ? atual - 1 : atual + 1];
  e.preventDefault();
  if (!proximo) return;

  proximo.focus();
  if (typeof proximo.select === 'function' && proximo.tagName.toUpperCase() === 'INPUT'
      && ['text', 'search', 'tel', 'email', 'number', 'password', 'url', ''].includes((proximo.type || '').toLowerCase())
      && !proximo.closest('.p-hidden-accessible')) {
    try { proximo.select(); } catch (err) { /* alguns tipos não permitem seleção */ }
  }
}

let instalado = false;

export default function instalarEnterComoTab() {
  if (instalado) return;
  instalado = true;
  // Fase de propagação (bubble): roda depois dos tratamentos do React e dos componentes PrimeReact
  document.addEventListener('keydown', aoTeclar);
}
